import FilterAltIcon from '@mui/icons-material/FilterAlt'
import SearchIcon from '@mui/icons-material/Search'
import { Grid, InputAdornment, MenuItem, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import { useEffect, useState } from 'react'

import { Dayjs } from 'dayjs'
import ExportExcel from '../../../components/Export'
import {
  EOrderDateType,
  EOrderStatus,
  EOrderType,
  ESortOrder,
} from '../../../types/order'
import OrderFilterDrawer, {
  EOrderOverdue,
  EStatusOrderShowTracking,
  EStatusTypeOrderCrmTelesale,
} from './OrderFilterDrawer'
import { IMerchant } from '../../../types/merchant'
import { axiosInstance } from '../../../hooks/useAxios'
import { DATE_TYPES } from '../../../constants/global'
import DateRangePicker from '../../../components/DateRangePicker'
import {
  SHIPPING_PROVIDER,
  SHIPPING_STATUS,
} from '../../../constants/shipping-status'
import { AllowShippingProviderLists } from '../../../utils/provider'
import { useUser } from '../../../hooks/useUser'
import { ImageLinks, LogoTypeProvider } from '../../../constants/image-link'

export interface IFilterMapped {
  page?: number
  perPage?: number
  search?: string
  searchType?: string
  status?: EOrderStatus | string
  quantity?: number
  startDate?: string
  endDate?: string
  actor?: string
  comission?: string
  categories?: string[]
  categoryObjectIds?: string[]
  products?: string[]
  productGroups?: string[]
  colors?: string
  sizes?: string
  distributionChannels?: string[]
  minTotal?: number
  maxTotal?: number
  statusShowCrmTelesale?: EStatusTypeOrderCrmTelesale
  dateType?: EOrderDateType
  paymentMethod?: EOrderType | string
  province?: string
  telesale?: string
  overdue?: EOrderOverdue
  statusOrderShowTracking?: EStatusOrderShowTracking
  assignTelesaleBy?: string
  statusCall?: string
  isPrinted?: 'printed' | 'unprinted' | null | string
  trackingProvider?: string
  providerStatus?: string
  trackingNumber?: string
  ignoreCancel?: string
  isDuplicate?: 'true' | 'false' | null | string
  nextCursor?: string
  prevCursor?: string
  sortOrder?: ESortOrder
}

interface FilterProps {
  filter: IFilterMapped
  status?: EOrderStatus
  onFilterChange: (filter: IFilterMapped) => void
  onSearch: (filter: IFilterMapped) => void
  showStatusCall?: boolean
  onDownload?: (date: Dayjs) => void
}

const OrderFilter = (props: FilterProps) => {
  const {
    onFilterChange,
    filter,
    onSearch,
    showStatusCall = false,
    onDownload,
    status,
  } = props
  const { search, trackingProvider } = filter
  const [isOpenedFilterDrawer, setIsOpenedFilterDrawer] =
    useState<boolean>(false)

  const { merchant } = useUser()

  const getProviderStatusOptions = () => {
    const provider = SHIPPING_STATUS.find(
      (p) => p.provider === trackingProvider,
    )
    return provider ? provider.status : []
  }

  const isAiStatus = status === EOrderStatus.AI
  const disableInput = isAiStatus && filter.isDuplicate === 'true'

  return (
    <>
      <OrderFilterDrawer
        isOpen={isOpenedFilterDrawer}
        onClose={() => setIsOpenedFilterDrawer(false)}
        onFilterChange={onFilterChange}
        filter={filter}
        onSearch={onSearch}
        hideStatusCall={showStatusCall}
      />
      <Grid container spacing={1} mb={2}>
        {isAiStatus && (
          <Grid item xs={12} lg={2}>
            <TextField
              label={'คำสั่งซื้อซ้ำ (จากหมายเลขโทรศัพท์)'}
              fullWidth
              id="isDuplicate"
              name="isDuplicate"
              variant="filled"
              value={filter.isDuplicate || 'false'}
              onChange={(e) =>
                onFilterChange({ ...filter, isDuplicate: e.target.value })
              }
              select
            >
              <MenuItem value="false">ไม่ใช่</MenuItem>
              <MenuItem value="true">ใช่</MenuItem>
            </TextField>
          </Grid>
        )}
        <Grid item xs={12} lg={2}>
          <TextField
            label={'ค้นหาจาก'}
            disabled={disableInput}
            fullWidth
            id="searchType"
            name="searchType"
            variant="filled"
            value={filter.searchType || 'customer_name'}
            onChange={(e) =>
              onFilterChange({
                ...filter,
                searchType: e.target.value,
                search: undefined,
              })
            }
            select
          >
            <MenuItem value="order_id">รหัสคำสั่งซื้อ</MenuItem>
            <MenuItem value="order_code">เลขออเดอร์ </MenuItem>
            <MenuItem value="customer_name">ชื่อลูกค้า</MenuItem>
            <MenuItem value="customer_phone">หมายเลขโทรศัพท์</MenuItem>
            <MenuItem value="tracking_number">หมายเลขพัสดุ</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} lg={2}>
          <TextField
            disabled={disableInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            label={'คำค้น'}
            fullWidth
            id="search"
            name="search"
            variant="filled"
            value={search || ''}
            onChange={(e) =>
              onFilterChange({ ...filter, search: e.target.value })
            }
          />
        </Grid>
        <Grid item xs={12} lg={2}>
          <TextField
            label="ค้นหาจากวันที่"
            select
            value={filter.dateType || ''}
            onChange={(e) =>
              onFilterChange({
                ...filter,
                dateType: e?.target.value as EOrderDateType,
              })
            }
            fullWidth
          >
            <MenuItem disabled value="" selected>
              <em>เลือกรายการค้นหาจากวันที่</em>
            </MenuItem>
            {DATE_TYPES.map((item, index: number) => (
              <MenuItem value={item.value} key={index}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} lg={4}>
          <DateRangePicker
            gap={1}
            startDate={filter.startDate}
            endDate={filter.endDate}
            onChange={(startDate, endDate) => {
              onFilterChange({ ...filter, startDate, endDate })
            }}
          />
        </Grid>
        <Grid item xs={12} lg={2}>
          <TextField
            label="ช่องทางการขนส่ง"
            id="trackingProvider"
            name="trackingProvider"
            value={filter.trackingProvider || ''}
            onChange={(e) =>
              onFilterChange({
                ...filter,
                trackingProvider: e.target.value as SHIPPING_PROVIDER,
              })
            }
            fullWidth
            select
          >
            <MenuItem disabled value="" selected>
              <em>ช่องทางการขนส่ง</em>
            </MenuItem>
            <MenuItem value="all" key="all">
              ทั้งหมด
            </MenuItem>
            {AllowShippingProviderLists(merchant).map((item, index: number) => (
              <MenuItem value={item.value} key={index}>
                {item.value !== 'all' && (
                  <img
                    style={{ marginRight: 5 }}
                    src={
                      ImageLinks[
                        `LOGO_${item.value.toUpperCase()}` as LogoTypeProvider
                      ]
                    }
                    alt="smallLogoSocial"
                    height={20}
                  />
                )}
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {trackingProvider && trackingProvider !== 'all' && (
          <>
            <Grid item xs={12} lg={2}>
              <TextField
                id="providerStatus"
                name="providerStatus"
                label="สถานะการขนส่ง"
                value={filter.providerStatus || ''}
                onChange={(e) =>
                  onFilterChange({
                    ...filter,
                    providerStatus: e.target.value as SHIPPING_PROVIDER,
                  })
                }
                fullWidth
                select
              >
                <MenuItem disabled value="" selected>
                  <em>สถานะการขนส่ง</em>
                </MenuItem>
                <MenuItem value={'all'}>ทั้งหมด</MenuItem>
                {getProviderStatusOptions().map(
                  (
                    item: {
                      code: number | string
                      name: string
                      status: string
                    },
                    index: number,
                  ) => (
                    <MenuItem value={item.code} key={index}>
                      {item.name}
                    </MenuItem>
                  ),
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} lg={2}>
              <TextField
                id="isPrinted"
                name="isPrinted"
                label="สถานะการพิมพ์"
                select
                value={filter.isPrinted || ''}
                onChange={(e) => {
                  onFilterChange({
                    ...filter,
                    isPrinted: e.target.value as 'printed' | 'unprinted',
                  })
                }}
                fullWidth
              >
                <MenuItem disabled value="" selected>
                  <em>เลือกสถานะ</em>
                </MenuItem>
                <MenuItem value={'all'}>ทั้งหมด</MenuItem>
                <MenuItem value={'printed'}>พิมพ์แล้ว</MenuItem>
                <MenuItem value={'unprinted'}>ยังไม่พิมพ์</MenuItem>
              </TextField>
            </Grid>
          </>
        )}
        {showStatusCall && (
          <Grid item xs={12} lg={2}>
            <TextField
              fullWidth
              label="สถานะการโทร"
              select
              onChange={(e) =>
                onFilterChange({ ...filter, statusCall: e.target.value })
              }
            >
              <MenuItem value={'all'}>ทั้งหมด</MenuItem>
              <MenuItem value={'called'}>โทรแล้ว</MenuItem>
              <MenuItem value={'cancelled'}>ไม่รับสาย</MenuItem>
              <MenuItem value={'not_called'}>ยังไม่โทร</MenuItem>
            </TextField>
          </Grid>
        )}
        <Grid item xs={12} lg={2}>
          <Button
            color="inherit"
            size="large"
            fullWidth
            variant="contained"
            startIcon={<FilterAltIcon />}
            onClick={() => {
              setIsOpenedFilterDrawer(true)
            }}
            sx={{ height: '100%' }}
          >
            ค้นหาโดยละเอียด
          </Button>
        </Grid>
        <Grid item xs={12} lg={2}>
          <Button
            color="primary"
            size="large"
            fullWidth
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => onSearch(filter)}
            sx={{ height: '100%' }}
          >
            ค้นหา
          </Button>
        </Grid>
        <Grid item xs={12} lg={2}>
          <ExportExcel onDownload={onDownload} />
        </Grid>
      </Grid>
    </>
  )
}

export default OrderFilter
